import { AiChatMessage, type MessagesHistoryType } from 'src/@types/ai';
import { UserType } from 'src/@types/workspace-info';
import { useAuthContext } from 'src/auth/useAuthContext';
import { useAIInputStore } from 'src/features/AIInput';
import { useSelector } from 'src/redux/store';
import randomUUID from 'src/utils/uuidv4';
import { getContextFilesMessages } from '../../../shared/utils';
import { useCurrentConversation } from '../../state/useCurrentConversation';
import { useAIResponseStore } from '../AIMessages/useAIResponseState';
import { useAIMessageController } from './useAIMessageController';

export const useSendAIMessage = () => {
  const { user } = useAuthContext();
  const store = useAIInputStore();

  const { think, doSearch, contextFiles, setInputValue, setIsLoading } = store(
    (state) => ({
      think: state.think,
      doSearch: state.doSearch,
      contextFiles: state.contextFiles,
      setInputValue: state.setInputValue,
      setIsLoading: state.setIsLoading,
    }),
  );

  const coperaUser = useSelector((state) =>
    state.workspace.workspace.users.find((u) => u.type === UserType.AI),
  );

  const { currentConversation, setCurrentConversation } =
    useCurrentConversation();

  const { sendAIChatMessage } = useAIMessageController(store);

  const { addMessage, setMessages, resetAIResponse } = useAIResponseStore();

  const handleSendAIMessage = async ({
    content,
    newConversation = false,
    createConversation = true,
    contextMessage,
    messagesHistory,
  }: {
    content: string;
    newConversation?: boolean;
    createConversation?: boolean;
    contextMessage?: string;
    messagesHistory?: MessagesHistoryType[];
  }) => {
    let conversation = newConversation ? null : currentConversation;
    if (!conversation && createConversation) {
      setMessages([]);
    }
    resetAIResponse();
    setIsLoading(true);
    setInputValue('');

    const now = new Date().toISOString();

    const userMessage = {
      content,
      role: 'user',
      _id: randomUUID(),
      user: user!._id,
      contextFiles: getContextFilesMessages(contextFiles),
      workspace: '',
      chat: conversation?._id || '',
      createdAt: now,
    } as AiChatMessage;
    addMessage(userMessage);

    const assistantMessage = {
      content: '',
      role: 'assistant',
      _id: randomUUID(),
      status: think || doSearch ? 'thinking' : 'loading',
      doSearch: doSearch,
      think: think,
      user: coperaUser!._id,
      workspace: '',
      chat: conversation?._id || '',
      createdAt: now,
    } as AiChatMessage;

    if (!conversation && createConversation) {
      setCurrentConversation({
        _id: 'loading',
        owner: user!._id,
        workspace: '',
        messages: [],
        createdAt: now,
        title: '...creating conversation',
        updatedAt: now,
      });
    }

    addMessage(assistantMessage);
    await sendAIChatMessage({
      userMessage,
      contextMessage,
      createConversation: createConversation ? !conversation : false,
      chatRef: conversation?._id,
      messagesHistory,
    });
    setIsLoading(false);
  };

  return {
    handleSendAIMessage,
    store,
  };
};
